import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const Sidebar = ({ isCollapsed, toggleSidebar }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isCoursesOpen, setIsCoursesOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleCoursesMenu = () => {
    setIsCoursesOpen(!isCoursesOpen);
  };

  const menuItems = [
    { path: '/admin', icon: '📊', label: 'Dashboard', roles: ['admin', 'editor', 'viewer'] },
    { path: '/admin/graduation-days', icon: '🎓', label: 'Graduation Days', roles: ['admin', 'editor'] },
    { path: '/admin/countdown', icon: '⏱️', label: 'Graduation Countdown', roles: ['admin', 'editor'] },
    { path: '/admin/departments', icon: '🏛️', label: 'Departments', roles: ['admin'] },
    {
      label: 'Courses',
      icon: '📚',
      roles: ['admin', 'editor'],
      isDropdown: true,
      children: [
        { path: '/admin/courses', icon: '📖', label: 'All Courses', roles: ['admin', 'editor'] },
        { path: '/admin/course-levels', icon: '📊', label: 'Course Levels', roles: ['admin'] },
        { path: '/admin/exam-bodies', icon: '🏛️', label: 'Exam Bodies', roles: ['admin'] }
      ]
    },
    { path: '/admin/graduates', icon: '👨‍🎓', label: 'Graduates', roles: ['admin', 'editor', 'viewer'] },
    { path: '/admin/gowns', icon: '👗', label: 'Gown Requests', roles: ['admin', 'editor'] },
    { path: '/admin/booklets', icon: '📘', label: 'Booklets', roles: ['admin', 'editor'] },
    { path: '/admin/alumni', icon: '👥', label: 'Alumni Committee', roles: ['admin', 'editor'] },
    { path: "/admin/registered-alumni", icon: "📝", label: "Registered Alumni", roles: ["admin", "editor"] },
    { path: "/admin/nominations", icon: "🗳️", label: "Nominations", roles: ["admin", "editor"] },
    { path: '/admin/reports', icon: '📈', label: 'Reports', roles: ['admin', 'editor', 'viewer'] },
    { path: '/admin/blog', icon: '📝', label: 'Blog & News', roles: ['admin', 'editor'] },
    { path: '/admin/users', icon: '👥', label: 'Users', roles: ['admin'] },
  ];

  // Filter menu items based on user role
  const filteredMenuItems = menuItems.filter(item => {
    if (item.isDropdown) {
      // For dropdown, check if user has access to any child
      return item.children.some(child => child.roles.includes(user?.role || 'viewer'));
    }
    return item.roles.includes(user?.role || 'viewer');
  });

  // Filter dropdown children based on role
  const getFilteredChildren = (children) => {
    return children.filter(child => child.roles.includes(user?.role || 'viewer'));
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className={`bg-primary text-white transition-all duration-300 hidden md:block ${
          isCollapsed ? 'w-20' : 'w-64'
        }`}
      >
        <div className="h-full flex flex-col">
          <div className="p-4 border-b border-primary-light flex items-center justify-between">
            {!isCollapsed && (
              <div>
                <h2 className="font-bold text-lg">Admin Panel</h2>
                <p className="text-secondary text-xs">{user?.name || 'Administrator'}</p>
                <p className="text-gray-400 text-xs mt-1">Role: {user?.role?.toUpperCase()}</p>
              </div>
            )}
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-lg hover:bg-primary-light transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isCollapsed ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                )}
              </svg>
            </button>
          </div>

          <nav className="flex-1 py-6 overflow-y-auto">
            <ul className="space-y-2">
              {filteredMenuItems.map((item, index) => (
                <li key={index}>
                  {item.isDropdown ? (
                    <div>
                      {/* Dropdown Header */}
                      <button
                        onClick={!isCollapsed ? toggleCoursesMenu : undefined}
                        className={`flex items-center w-full px-4 py-3 transition-colors hover:bg-primary-light ${
                          isCollapsed ? 'justify-center' : 'justify-between'
                        }`}
                        title={isCollapsed ? item.label : ''}
                      >
                        <div className="flex items-center space-x-3">
                          <span className="text-xl">{item.icon}</span>
                          {!isCollapsed && <span className="text-sm font-medium">{item.label}</span>}
                        </div>
                        {!isCollapsed && (
                          <svg
                            className={`w-4 h-4 transition-transform ${isCoursesOpen ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </button>

                      {/* Dropdown Menu */}
                      {!isCollapsed && isCoursesOpen && (
                        <ul className="ml-8 mt-1 space-y-1">
                          {getFilteredChildren(item.children).map((child, childIndex) => (
                            <li key={childIndex}>
                              <NavLink
                                to={child.path}
                                className={({ isActive }) =>
                                  `flex items-center px-4 py-2 rounded-lg transition-colors ${
                                    isActive
                                      ? 'bg-secondary text-gray-900'
                                      : 'hover:bg-primary-light text-white'
                                  }`
                                }
                              >
                                <span className="text-base mr-2">{child.icon}</span>
                                <span className="text-xs">{child.label}</span>
                              </NavLink>
                            </li>
                          ))}
                        </ul>
                      )}

                      {/* Collapsed mode - show tooltip with dropdown */}
                      {isCollapsed && (
                        <div className="relative group">
                          <NavLink
                            to="#"
                            className="flex items-center justify-center px-4 py-3 hover:bg-primary-light"
                            title={item.label}
                          >
                            <span className="text-xl">{item.icon}</span>
                          </NavLink>
                          <div className="absolute left-full top-0 ml-2 hidden group-hover:block bg-primary rounded-lg shadow-lg py-2 min-w-40 z-50">
                            {getFilteredChildren(item.children).map((child, childIndex) => (
                              <NavLink
                                key={childIndex}
                                to={child.path}
                                className={({ isActive }) =>
                                  `block px-4 py-2 text-sm whitespace-nowrap ${
                                    isActive
                                      ? 'bg-secondary text-gray-900'
                                      : 'hover:bg-primary-light text-white'
                                  }`
                                }
                              >
                                {child.icon} {child.label}
                              </NavLink>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <NavLink
                      to={item.path}
                      className={({ isActive }) =>
                        `flex items-center px-4 py-3 transition-colors ${
                          isActive
                            ? 'bg-secondary text-gray-900 border-r-4 border-secondary'
                            : 'hover:bg-primary-light text-white'
                        } ${isCollapsed ? 'justify-center' : 'space-x-3'}`
                      }
                      title={isCollapsed ? item.label : ''}
                    >
                      <span className="text-xl">{item.icon}</span>
                      {!isCollapsed && <span className="text-sm font-medium">{item.label}</span>}
                    </NavLink>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          <div className="p-4 border-t border-primary-light">
            <button
              onClick={handleLogout}
              className={`flex items-center w-full px-4 py-3 rounded-lg hover:bg-red-600 transition-colors ${
                isCollapsed ? 'justify-center' : 'space-x-3'
              }`}
            >
              <span className="text-xl">🚪</span>
              {!isCollapsed && <span className="text-sm font-medium">Logout</span>}
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-primary shadow-lg z-50">
        <div className="flex justify-around py-2">
          <NavLink to="/admin" className="flex flex-col items-center py-2 px-3 text-white">
            <span className="text-xl">📊</span>
            <span className="text-xs mt-1">Home</span>
          </NavLink>
          <NavLink to="/admin/graduates" className="flex flex-col items-center py-2 px-3 text-white">
            <span className="text-xl">👨‍🎓</span>
            <span className="text-xs mt-1">Grads</span>
          </NavLink>
          <NavLink to="/admin/booklets" className="flex flex-col items-center py-2 px-3 text-white">
            <span className="text-xl">📘</span>
            <span className="text-xs mt-1">Booklets</span>
          </NavLink>
          {user?.role === 'admin' && (
            <NavLink to="/admin/users" className="flex flex-col items-center py-2 px-3 text-white">
              <span className="text-xl">👥</span>
              <span className="text-xs mt-1">Users</span>
            </NavLink>
          )}
          <button
            onClick={handleLogout}
            className="flex flex-col items-center py-2 px-3 text-white"
          >
            <span className="text-xl">🚪</span>
            <span className="text-xs mt-1">Exit</span>
          </button>
        </div>
      </nav>

      <div className="md:hidden h-16"></div>
    </>
  );
};

export default Sidebar;
